/**
 * Form-internal types + constants for `QuestionEditForm` and its section
 * components. Extracted during the 2026-05-17 split so the section files
 * (sections/*.tsx) can import these without circling back through the
 * QuestionEditForm shell.
 *
 * `QuestionFormData` is the public type re-exported from `QuestionEditForm`
 * and from this folder's `index.ts`.
 */
import type { QuestionFigure } from '@padcanvas/shared';
import type { MCQOption, ExamAppearance, Solution } from '../questions-page';
import type { QuestionPart, PartRequirement, QuestionSourceRegion } from '@padcanvas/shared';
import { READINESS_LEVELS } from '../question-set-types';
import { QUESTION_TYPES } from '@padcanvas/shared';

export type QuestionType = (typeof QUESTION_TYPES)[number];

/** Display labels used by the admin parts editor. The order is the partId
 *  picking order — `nextPartId` walks this and returns the first free letter.
 *  Stable across edits: removing a part never shifts other partIds. */
export const PART_LABELS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l'] as const;

/** Pick the first letter not already used as a partId. Fallback for >12
 *  parts is a synthetic `p<N>` — shouldn't occur in practice. */
export function nextPartId(existing: ReadonlyArray<QuestionPart>): string {
    const used = new Set(existing.map(p => p.partId));
    for (const l of PART_LABELS) if (!used.has(l)) return l;
    return `p${existing.length + 1}`;
}

/** MCQ option-label picking order — `addOption` finds the first letter not
 *  in use. */
export const MCQ_LABELS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;

/** Set of readiness values that the admin form's select treats as valid;
 *  values outside this set fall back to 0 (Idea). */
export const VALID_READINESS_VALUES = new Set<number>(READINESS_LEVELS.map(r => r.value));

/** A concept option for the tagging pickers. Supplied by the host app
 *  (admin-console) via `onLoadConcepts`; `aliases` power the typeahead. */
export interface ConceptOption {
    id: string;
    name: string;
    aliases?: string[];
}

export interface RelatedQuestion {
    questionId: string;
    relationType: string;
    reasonString: string;
    selectionCriteria?: Record<string, unknown>;
}

/** Shape the form binds to. Public — exported from the folder's `index.ts`
 *  (and via a back-compat re-export from `QuestionEditForm.tsx`). */
// Question origin (see docs/FeatureDesigns/question-pools-and-entitlement/question-source-type-target.md). Local
// mirror of the backend QuestionSourceType / BookRef (shared-ui keeps its own
// UI-facing type view).
export type QuestionSourceType =
    | 'previous-year-paper' | 'textbook' | 'ai-generated' | 'online' | 'manual' | 'unknown';
export const QUESTION_SOURCE_TYPES: QuestionSourceType[] = [
    'previous-year-paper', 'textbook', 'ai-generated', 'online', 'manual', 'unknown',
];
export interface BookRef {
    bookId: string;
    bookName: string;    // denormalized Book.title for display
    location?: string;   // free text: "Ch 4 · Ex 1.2 · Q15"
}
// Book registry option supplied by the host (via onLoadBooks) for the picker.
export interface BookOption {
    id: string;
    title: string;
    author?: string;
    publisher?: string;
}

export interface QuestionFormData {
    subject: string;
    questionText: string;
    correctAnswer: string;
    questionType?: QuestionType;
    errorMargin?: number;
    difficulty?: string;
    options?: MCQOption[];
    solutions?: Solution[];
    hints?: string[];
    maxHintsForStudent?: number;
    appearedInExams?: ExamAppearance[];
    targetExams?: string[];
    /** Concept ids this question uses (Phase 2 tagging; question-level). */
    usesConcepts?: string[];
    /** Concept ids to ALWAYS avoid in hints for this question — L3 hard override. */
    avoidConcepts?: string[];
    /** Canonical module reference (single source of truth; replaces legacy moduleId). */
    module?: { id: string; name: string };
    questionReadiness?: number;
    enrichStatus?: number;
    enrichmentNotes?: string;
    questionSource?: string;
    /** Origin of the question (origin axis, not ingestion). */
    sourceType?: QuestionSourceType;
    /** textbook origin — reference(s) into the book registry. */
    bookSources?: BookRef[];
    /** online origin — where on the web. */
    sourceUrl?: string;
    questionDiagramUrl?: string | null;
    /** Figures the text places by `[[fig:id]]` marker. See FiguresEditor. */
    figures?: QuestionFigure[];
    /** SHA-256 of the image at questionDiagramUrl, when the editor knows it (a
     *  crop from the paper). Stale values are dropped server-side on save. */
    questionDiagramHash?: string;
    solutionImageUrl?: string | null;
    solutionImageHash?: string;
    /** Where each image was cut from on the source paper — carried so a save
     *  keeps it, and so "Crop from paper" opens on the right region.
     *  See docs/FeatureDesigns/question-image-recrop-target.md. */
    sourceRegions?: QuestionSourceRegion[];
    relatedQuestions?: RelatedQuestion[];
    /** Which (board, grade) pools this question serves — the authority.
     *  See docs/FeatureDesigns/question-pools-and-entitlement/question-pools-and-programs-target.md §3. */
    boardGrades?: string[];
    /** DERIVED from `boardGrades` at the write funnel (retirement plan step E).
     *  Read-only in the editor: whatever is sent here is overwritten on save. */
    class?: string[];
    marks?: number;
    keywords?: string[];
    /** Set when questionType === 'STRUCTURED'. Each entry is a sub-part
     *  with its own statement and marks. Phase 1A: all parts are FREEFORM. */
    parts?: QuestionPart[];
    /** STRUCTURED choice / K-of-N expression (Phase 1C). Absent ⇒ every part
     *  required (implicit `all`). See PartRequirementEditor. */
    requirement?: PartRequirement;
}

export type UpdateField = <K extends keyof QuestionFormData>(field: K, value: QuestionFormData[K]) => void;
